import React, { useState, useEffect, useMemo } from 'react';
import { 
    Box, 
    Typography, 
    Tabs, 
    Tab, 
    Card, 
    CardContent, 
    CardHeader,
    Button,
    Alert,
    Badge
} from '@mui/material';
import { 
    Business as BusinessIcon,
    Download as DownloadIcon,
    Upload as UploadIcon,
    Info as InfoIcon,
    Block as BlockIcon,
    Schedule as ScheduleIcon
} from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import { TreasuryDataGrid } from '../DataGrid/TreasuryDataGrid';
import { treasurySpanishLocale } from '../../utils/localization';

interface ProveedorNoPago {
    id: string;
    Nit: string;
    Nombre: string;
    Observaciones: string;
}

interface ProveedorPlazoExtendido {
    id: string;
    Nit: string;
    Nombre: string;
    Plazo: string;
}

interface ActualizarProveedoresData {
    proveedores_no_pago: ProveedorNoPago[];
    proveedores_plazo_extendido: ProveedorPlazoExtendido[];
    active_tab?: string;
    success_message?: string;
}

interface ActualizarProveedoresResultProps {
    data?: ActualizarProveedoresData;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`provider-tabpanel-${index}`}
            aria-labelledby={`provider-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `provider-tab-${index}`,
        'aria-controls': `provider-tabpanel-${index}`,
    };
}

export function ActualizarProveedoresResult({ data }: ActualizarProveedoresResultProps) {
    const [activeTab, setActiveTab] = useState<number>(0);
    const [proveedoresNoPago, setProveedoresNoPago] = useState<ProveedorNoPago[]>([]);
    const [proveedoresPlazoExtendido, setProveedoresPlazoExtendido] = useState<ProveedorPlazoExtendido[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Set initial tab based on data
    useEffect(() => {
        if (data?.active_tab === 'plazo_extendido') {
            setActiveTab(1);
        } else {
            setActiveTab(0);
        }
    }, [data?.active_tab]);

    // Load data on component mount
    useEffect(() => {
        loadProveedoresData();
    }, []);

    const loadProveedoresData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Load both datasets concurrently
            const [noPagoResponse, plazoExtendidoResponse] = await Promise.all([
                fetch('/obtener-proveedores-no-pago-actuales'),
                fetch('/obtener-proveedores-plazo-extendido-actuales')
            ]);

            if (!noPagoResponse.ok || !plazoExtendidoResponse.ok) {
                throw new Error('Error al cargar los datos de proveedores');
            }

            const noPagoData = await noPagoResponse.json();
            const plazoExtendidoData = await plazoExtendidoResponse.json();

            // Add IDs to the data
            const noPagoWithIds = noPagoData.map((item: any, index: number) => ({
                ...item,
                id: `no_pago_${item.Nit}_${index}`
            }));

            const plazoExtendidoWithIds = plazoExtendidoData.map((item: any, index: number) => ({
                ...item,
                id: `plazo_extendido_${item.Nit}_${index}`
            }));

            setProveedoresNoPago(noPagoWithIds);
            setProveedoresPlazoExtendido(plazoExtendidoWithIds);

        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error desconocido');
        } finally {
            setLoading(false);
        }
    };

    // Column definitions for Proveedores No Pago
    const noPagoColumns: GridColDef[] = useMemo(() => [
        {
            field: 'Nit',
            headerName: 'NIT',
            width: 140,
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'Nombre',
            headerName: 'Nombre',
            width: 300,
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'Observaciones',
            headerName: 'Observaciones',
            width: 400,
            align: 'left',
            headerAlign: 'left',
        },
    ], []);

    // Column definitions for Proveedores Plazo Extendido
    const plazoExtendidoColumns: GridColDef[] = useMemo(() => [
        {
            field: 'Nit',
            headerName: 'NIT',
            width: 140,
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'Nombre',
            headerName: 'Nombre',
            width: 300,
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'Plazo',
            headerName: 'Plazo',
            width: 150,
            align: 'center',
            headerAlign: 'center',
        },
    ], []);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleExportNoPago = () => {
        window.location.href = '/download-proveedores-excel?tipo=no_pago';
    };

    const handleExportPlazoExtendido = () => {
        window.location.href = '/download-proveedores-excel?tipo=plazo_extendido';
    };

    const handleUploadNoPago = () => {
        const fileInput = document.getElementById('fileNoPago') as HTMLInputElement;
        if (fileInput) {
            fileInput.click();
        }
    };

    const handleUploadPlazoExtendido = () => {
        const fileInput = document.getElementById('filePlazoExtendido') as HTMLInputElement;
        if (fileInput) {
            fileInput.click();
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6">Cargando datos de proveedores...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="h6">Error</Typography>
                    <Typography>{error}</Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {/* Success Message */}
            {data?.success_message && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    <InfoIcon sx={{ mr: 1 }} />
                    {data.success_message}
                </Alert>
            )}

            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
                    <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
                    Actualizar Proveedores
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={activeTab === 0 ? handleExportNoPago : handleExportPlazoExtendido}
                    sx={{ minWidth: 150 }}
                >
                    Descargar Excel
                </Button>
            </Box>

            {/* Tabs */}
            <Card>
                <CardHeader 
                    title={
                        <Tabs 
                            value={activeTab} 
                            onChange={handleTabChange}
                            aria-label="Tabs de proveedores"
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                },
                            }}
                        >
                            <Tab 
                                icon={<BlockIcon />} 
                                iconPosition="start"
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        Proveedores No Pago
                                        <Badge 
                                            badgeContent={proveedoresNoPago.length} 
                                            color="primary"
                                            sx={{ ml: 1 }}
                                        />
                                    </Box>
                                }
                                {...a11yProps(0)} 
                            />
                            <Tab 
                                icon={<ScheduleIcon />} 
                                iconPosition="start"
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        Proveedores Plazo Extendido
                                        <Badge 
                                            badgeContent={proveedoresPlazoExtendido.length} 
                                            color="primary"
                                            sx={{ ml: 1 }}
                                        />
                                    </Box>
                                }
                                {...a11yProps(1)} 
                            />
                        </Tabs>
                    }
                />
                <CardContent>
                    {/* Proveedores No Pago Tab */}
                    <TabPanel value={activeTab} index={0}>
                        <Box sx={{ mb: 3 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <InfoIcon sx={{ mr: 1 }} />
                                <Typography variant="body2">
                                    <strong>Proveedores No Pago:</strong> Estos proveedores están marcados como NO PAGO en el sistema y no se incluirán en las programaciones de pagos automáticas.
                                </Typography>
                            </Alert>
                            
                            {/* Upload Section */}
                            <Box sx={{ 
                                bgcolor: 'grey.50', 
                                borderRadius: 2, 
                                p: 3, 
                                mb: 3, 
                                border: '2px dashed',
                                borderColor: 'grey.300',
                                '&:hover': { borderColor: 'success.main' }
                            }}>
                                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                    <UploadIcon sx={{ mr: 1 }} />
                                    Cargar archivo de Proveedores No Pago
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Button 
                                        variant="contained" 
                                        color="success"
                                        startIcon={<UploadIcon />}
                                        onClick={handleUploadNoPago}
                                        sx={{ minWidth: 200 }}
                                    >
                                        Seleccionar archivo Excel
                                    </Button>
                                    <Typography variant="body2" color="text.secondary">
                                        <InfoIcon sx={{ mr: 1, fontSize: 'small' }} />
                                        El archivo debe contener las columnas: Nit, Nombre, Observaciones
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <TreasuryDataGrid
                            columns={noPagoColumns}
                            rows={proveedoresNoPago}
                            title="Proveedores No Pago"
                            tableId="proveedores-no-pago"
                            showToolbar={true}
                            showExport={true}
                            showRefresh={true}
                            enableStatePersistence={true}
                            onExport={handleExportNoPago}
                            onRefresh={loadProveedoresData}
                            localeText={treasurySpanishLocale}
                            sx={{
                                '& .MuiDataGrid-root': {
                                    border: 'none',
                                },
                                '& .MuiDataGrid-cell:focus': {
                                    outline: 'none',
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: 'rgba(32, 58, 93, 0.04)',
                                },
                            }}
                        />
                    </TabPanel>

                    {/* Proveedores Plazo Extendido Tab */}
                    <TabPanel value={activeTab} index={1}>
                        <Box sx={{ mb: 3 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <InfoIcon sx={{ mr: 1 }} />
                                <Typography variant="body2">
                                    <strong>Proveedores Plazo Extendido:</strong> Estos proveedores tienen plazos de pago extendidos que se aplicarán automáticamente en las programaciones de pagos.
                                </Typography>
                            </Alert>
                            
                            {/* Upload Section */}
                            <Box sx={{ 
                                bgcolor: 'grey.50', 
                                borderRadius: 2, 
                                p: 3, 
                                mb: 3, 
                                border: '2px dashed',
                                borderColor: 'grey.300',
                                '&:hover': { borderColor: 'success.main' }
                            }}>
                                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                    <UploadIcon sx={{ mr: 1 }} />
                                    Cargar archivo de Proveedores Plazo Extendido
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Button 
                                        variant="contained" 
                                        color="success"
                                        startIcon={<UploadIcon />}
                                        onClick={handleUploadPlazoExtendido}
                                        sx={{ minWidth: 200 }}
                                    >
                                        Seleccionar archivo Excel
                                    </Button>
                                    <Typography variant="body2" color="text.secondary">
                                        <InfoIcon sx={{ mr: 1, fontSize: 'small' }} />
                                        El archivo debe contener las columnas: Nit, Nombre
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <TreasuryDataGrid
                            columns={plazoExtendidoColumns}
                            rows={proveedoresPlazoExtendido}
                            title="Proveedores Plazo Extendido"
                            tableId="proveedores-plazo-extendido"
                            showToolbar={true}
                            showExport={true}
                            showRefresh={true}
                            enableStatePersistence={true}
                            onExport={handleExportPlazoExtendido}
                            onRefresh={loadProveedoresData}
                            localeText={treasurySpanishLocale}
                            sx={{
                                '& .MuiDataGrid-root': {
                                    border: 'none',
                                },
                                '& .MuiDataGrid-cell:focus': {
                                    outline: 'none',
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: 'rgba(32, 58, 93, 0.04)',
                                },
                            }}
                        />
                    </TabPanel>
                </CardContent>
            </Card>

            {/* Hidden file inputs for upload functionality */}
            <input
                type="file"
                id="fileNoPago"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                        // Use the hidden form to submit
                        const form = document.getElementById('uploadFormNoPago') as HTMLFormElement;
                        const fileInput = form.querySelector('input[name="file"]') as HTMLInputElement;
                        
                        // Create a new FileList and assign it to the form's file input
                        const dataTransfer = new DataTransfer();
                        dataTransfer.items.add(file);
                        fileInput.files = dataTransfer.files;
                        
                        // Submit the form
                        form.submit();
                    }
                }}
            />
            <input
                type="file"
                id="filePlazoExtendido"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                        // Use the hidden form to submit
                        const form = document.getElementById('uploadFormPlazoExtendido') as HTMLFormElement;
                        const fileInput = form.querySelector('input[name="file"]') as HTMLInputElement;
                        
                        // Create a new FileList and assign it to the form's file input
                        const dataTransfer = new DataTransfer();
                        dataTransfer.items.add(file);
                        fileInput.files = dataTransfer.files;
                        
                        // Submit the form
                        form.submit();
                    }
                }}
            />
        </Box>
    );
}